import {Alert, Button, Divider, Group, Modal, Stack} from '@mantine/core';
import React from 'react';

import {AntibodyEnforcedSchemaSpec} from 'antibodies/schema-configuration/AntibodyEnforcedDataModelConstants';
import {enforcedFieldSpecToField} from 'antibodies/schema-configuration/AntibodyEnforcedSchemaSpecUtils';
import {AntibodyComponentSchemaInterfaceId} from 'antibodies/schema-configuration/AntibodySchemaInterfaceConstants';
import ConstraintMigrationPreview from 'antibodies/schema-configuration/schema-migration/ConstraintMigrationPreview';
import hasEnforcedDataModelChanges from 'antibodies/schema-configuration/schema-migration/hasEnforcedDataModelChanges';
import NamingStrategyMigrationPreview from 'antibodies/schema-configuration/schema-migration/NamingStrategyMigrationPreview';
import NewFieldsMigrationPreview from 'antibodies/schema-configuration/schema-migration/NewFieldsMigrationPreview';
import {
  ClientTagSchemaConstraint,
  RegistryLabelingStrategy,
  TagSchemaConstraint,
} from 'constants/RegistryConstants';

export interface AntibodyComponentMigrationPreviewModalProps {
  componentTypeName: string;
  componentInterfaceId: AntibodyComponentSchemaInterfaceId;
  registryId: string;
  enforcedSchemaSpec: AntibodyEnforcedSchemaSpec;
  existingConstraint: ClientTagSchemaConstraint | TagSchemaConstraint | null;
  existingNamingStrategies: Array<RegistryLabelingStrategy> | null;
  folderItemType: string | null;
  onClose: () => void;
  onApply: () => void;
}

/**
 * Confirmation modal shown before applying a protein component type to an existing schema.
 * Previews the enforced changes the migration will make and lets the user confirm or cancel.
 */
export default function AntibodyComponentMigrationPreviewModal({
  componentTypeName,
  componentInterfaceId,
  registryId,
  enforcedSchemaSpec,
  existingConstraint,
  existingNamingStrategies,
  folderItemType,
  onClose,
  onApply,
}: AntibodyComponentMigrationPreviewModalProps): JSX.Element {
  const {enforcedFieldSpecs, enforcedConstraintSpec, enforcedNamingStrategySpec} = enforcedSchemaSpec;
  const showsEnforcedChanges = hasEnforcedDataModelChanges(enforcedSchemaSpec);
  const enforcedFieldsToCreate = enforcedFieldSpecs.map((enforcedFieldSpec) =>
    enforcedFieldSpecToField(componentInterfaceId, enforcedFieldSpec, registryId)
  );

  return (
    <Modal
      title={`Apply ${componentTypeName} protein component type`}
      opened={true}
      onClose={onClose}
      size="lg"
    >
      <Stack spacing="lg">
        <Alert>
          {showsEnforcedChanges ? (
            <>
              Applying the {componentTypeName} protein component type makes the following changes to this
              schema. This action cannot be undone.
            </>
          ) : (
            <>
              Assigns the {componentTypeName} protein component type. No other schema changes. This action
              cannot be undone.
            </>
          )}
        </Alert>

        {showsEnforcedChanges && (
          <>
            <NewFieldsMigrationPreview
              fieldsToCreate={enforcedFieldsToCreate}
              enforcedFieldSpecs={enforcedFieldSpecs}
            />

            {enforcedConstraintSpec != null && (
              <ConstraintMigrationPreview
                existingConstraint={existingConstraint}
                enforcedConstraintSpec={enforcedConstraintSpec}
                enforcedFields={enforcedFieldsToCreate}
                folderItemType={folderItemType}
              />
            )}

            {enforcedNamingStrategySpec != null && (
              <NamingStrategyMigrationPreview
                enforcedNamingStrategySpec={enforcedNamingStrategySpec}
                existingNamingStrategies={existingNamingStrategies}
              />
            )}
          </>
        )}

        <Divider />

        <Group position="right">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onApply}>Apply</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
